import { useState, useEffect, useCallback } from 'react';
import { Landmark, Plus, Trash2, X, Building2, Layers, AlertTriangle } from 'lucide-react';

import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import {
  roleAccountApi, ACCOUNT_ROLES, PAYMENT_MODE_ROLES, roleLabel,
  type RoleAccountDto, type PaymentModeAccountDto, type RoleScopeType, type RequiredRoleDto,
} from '@/api/roleAccount.api';
import {
  integrationMappingApi,
  type IntAccountDto, type MappingCostCenterDto,
} from '@/api/integrationMapping.api';

const inputCls =
  'w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal';

export const PostingAccountsPage = () => {
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Permissions.CFG_TRANSACTION_TYPE_EDIT);

  const [rows, setRows] = useState<RoleAccountDto[]>([]);
  // Roles the company's own posting rules use. A pack can name roles the fixed list does not know
  // (GRNI, PPV, GOODS_IN_TRANSIT…); without them in the picker they could not be mapped at all.
  const [required, setRequired] = useState<RequiredRoleDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<IntAccountDto[]>([]);
  const [costCenters, setCostCenters] = useState<MappingCostCenterDto[]>([]);

  const [editing, setEditing] = useState(false);
  const [scopeType, setScopeType] = useState<RoleScopeType>('COST_CENTER');
  const [scopeCode, setScopeCode] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [accountRole, setAccountRole] = useState('CASH');
  const [accountId, setAccountId] = useState('');
  const [saving, setSaving] = useState(false);

  // Payment-mode accounts
  const [payModes, setPayModes] = useState<PaymentModeAccountDto[]>([]);
  const [payEditing, setPayEditing] = useState(false);
  const [pmMode, setPmMode] = useState('CASH');
  const [pmRole, setPmRole] = useState('CLEARING');
  const [pmCostCenterId, setPmCostCenterId] = useState('');
  const [pmAccountId, setPmAccountId] = useState('');
  const [pmSaving, setPmSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, accts, ccs, pm, req] = await Promise.all([
        roleAccountApi.list(),
        integrationMappingApi.accounts(),
        integrationMappingApi.costCenters(),
        roleAccountApi.paymentModes(),
        roleAccountApi.required(),
      ]);
      setRequired(req);
      setRows(list);
      setAccounts(accts);
      setCostCenters(ccs);
      setPayModes(pm);
    } catch (err) {
      console.error('Failed to load posting accounts', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = (role?: string) => {
    setScopeType(role ? 'COMPANY' : 'COST_CENTER');
    setCostCenterId('');
    setScopeCode('');
    setAccountRole(role ?? 'CASH');
    setAccountId('');
    setEditing(true);
  };

  const roleOptions = [...new Set([...ACCOUNT_ROLES, ...required.map(r => r.role)])].sort();
  const unmapped = required.filter(r => !r.mapped);

  const canSave = accountId && accountRole
    && (scopeType === 'COMPANY' || (scopeType === 'COST_CENTER' ? costCenterId : scopeCode.trim()));

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await roleAccountApi.upsert({
        scopeType,
        costCenterId: scopeType === 'COST_CENTER' ? costCenterId : null,
        scopeCode: scopeType === 'VOUCHER_GROUP' ? scopeCode.trim() : null,
        accountRole,
        accountId,
      });
      setEditing(false);
      await load();
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: RoleAccountDto) => {
    if (!confirm(`Remove ${r.scopeType === 'COMPANY' ? 'company default' : r.costCenterCode ?? `voucher category ${r.scopeCode}`} → ${r.accountRole}?`)) return;
    try { await roleAccountApi.remove(r.id); await load(); }
    catch (err) { console.error('Delete failed', err); }
  };

  const openPayAdd = () => { setPmMode('CASH'); setPmRole('CLEARING'); setPmCostCenterId(''); setPmAccountId(''); setPayEditing(true); };
  const canSavePay = pmMode.trim() && pmRole && pmAccountId;
  const savePay = async () => {
    if (!canSavePay) return;
    setPmSaving(true);
    try {
      await roleAccountApi.upsertPaymentMode({
        paymentModeCode: pmMode.trim(), accountRole: pmRole,
        costCenterId: pmCostCenterId || null, accountId: pmAccountId,
      });
      setPayEditing(false);
      await load();
    } catch (err) { console.error('Save failed', err); }
    finally { setPmSaving(false); }
  };
  const removePay = async (p: PaymentModeAccountDto) => {
    if (!confirm(`Remove ${p.paymentModeCode}/${p.accountRole}${p.costCenterCode ? ' @ ' + p.costCenterCode : ''}?`)) return;
    try { await roleAccountApi.removePaymentMode(p.id); await load(); }
    catch (err) { console.error('Delete failed', err); }
  };

  const company = rows.filter(r => r.scopeType === 'COMPANY');
  const overrides = rows.filter(r => r.scopeType !== 'COMPANY');

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Landmark size={20} className="text-signal" /> Posting Accounts
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Which GL account each posting role resolves to — company defaults and per-cost-centre overrides
              (e.g. cash-in-hand differs per store). Posting rules resolve their role legs against these.
            </p>
          </div>
          {canManage && (
            <button onClick={() => openAdd()}
              className="px-3 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg transition-colors flex items-center gap-1.5">
              <Plus size={15} /> Add override
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : (
          <>
            {unmapped.length > 0 && (
              <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                  <AlertTriangle size={14} /> Your live posting rules use {unmapped.length} role{unmapped.length === 1 ? '' : 's'} with no account yet.
                  Documents reaching them are refused until they are mapped.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {unmapped.map(r => (
                    <button key={r.role} type="button" disabled={!canManage} onClick={() => openAdd(r.role)}
                      title={`Used by ${r.usedBy.join(', ')}`}
                      className="px-2 py-0.5 rounded text-xs font-medium bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100 disabled:cursor-default">
                      {roleLabel(r.role)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Cost-centre overrides */}
            <section>
              <h2 className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Layers size={13} /> Cost-centre overrides ({overrides.length})
              </h2>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                {overrides.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-slate-400 text-center">
                    No overrides yet — every role uses the company default. Add one to route e.g. a store's cash to its own account.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Applies to</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Role</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">GL Account</th>
                        {canManage && <th className="px-4 py-2.5" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {overrides.map(r => (
                        <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                          <td className="px-4 py-2.5 text-slate-900 dark:text-white">
                            {r.scopeType === 'VOUCHER_GROUP'
                              ? <>Voucher category <span className="font-mono text-xs text-slate-500 dark:text-gray-400">{r.scopeCode}</span></>
                              : <><span className="font-mono text-xs text-slate-500 dark:text-gray-400">{r.costCenterCode}</span> {r.costCenterName}</>}
                          </td>
                          <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-medium" title={r.accountRole}>{roleLabel(r.accountRole)}</span></td>
                          <td className="px-4 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300">{r.accountCode} — {r.accountName}</td>
                          {canManage && (
                            <td className="px-4 py-2.5 text-right">
                              <button onClick={() => remove(r)} title="Remove override" className="p-1.5 rounded hover:bg-red-100 text-red-500">
                                <Trash2 size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            {/* Company defaults */}
            <section>
              <h2 className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Building2 size={13} /> Company defaults ({company.length})
              </h2>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Role</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">GL Account</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {company.map(r => (
                      <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                        <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-medium" title={r.accountRole}>{roleLabel(r.accountRole)}</span></td>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300">{r.accountCode} — {r.accountName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Payment-mode accounts */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Layers size={13} /> Payment-mode accounts ({payModes.length})
                </h2>
                {canManage && (
                  <button onClick={openPayAdd} className="text-xs font-medium text-signal hover:underline flex items-center gap-1"><Plus size={13} /> Add</button>
                )}
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                {payModes.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-slate-400 text-center">
                    No payment-mode accounts — e.g. map CASH→CLEARING to Cash on Hand, and add a cost-centre row to route a store's cash to its own account.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Mode</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Role</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Cost Centre</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">GL Account</th>
                        {canManage && <th className="px-4 py-2.5" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {payModes.map(p => (
                        <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                          <td className="px-4 py-2.5 font-mono text-xs text-slate-700 dark:text-slate-300">{p.paymentModeCode}</td>
                          <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-medium">{p.accountRole}</span></td>
                          <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-gray-400">{p.costCenterCode ?? <span className="text-slate-300">company</span>}</td>
                          <td className="px-4 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300">{p.accountCode} — {p.accountName}</td>
                          {canManage && (
                            <td className="px-4 py-2.5 text-right">
                              <button onClick={() => removePay(p)} title="Remove" className="p-1.5 rounded hover:bg-red-100 text-red-500"><Trash2 size={14} /></button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      {/* Payment-mode modal */}
      {payEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !pmSaving && setPayEditing(false)}>
          <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white">Payment-mode account</h3>
              <button onClick={() => !pmSaving && setPayEditing(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Payment mode code</label>
                <input value={pmMode} onChange={e => setPmMode(e.target.value.toUpperCase())} placeholder="CASH / CARD_VISA" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Role</label>
                <select value={pmRole} onChange={e => setPmRole(e.target.value)} className={inputCls}>
                  {PAYMENT_MODE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Cost Centre (optional — blank = company default)</label>
                <select value={pmCostCenterId} onChange={e => setPmCostCenterId(e.target.value)} className={inputCls}>
                  <option value="">Company default</option>
                  {costCenters.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">GL Account</label>
                <select value={pmAccountId} onChange={e => setPmAccountId(e.target.value)} className={inputCls}>
                  <option value="">Select an account…</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
              <button onClick={() => setPayEditing(false)} disabled={pmSaving} className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
              <button onClick={savePay} disabled={!canSavePay || pmSaving}
                className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                {pmSaving ? <><Spinner size="sm" /> Saving…</> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/edit override modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !saving && setEditing(false)}>
          <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white">Role account override</h3>
              <button onClick={() => !saving && setEditing(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Scope</label>
                <select value={scopeType} onChange={e => setScopeType(e.target.value as RoleScopeType)} className={inputCls}>
                  <option value="COST_CENTER">Cost centre override</option>
                  <option value="VOUCHER_GROUP">Voucher category</option>
                  <option value="COMPANY">Company default</option>
                </select>
              </div>
              {scopeType === 'VOUCHER_GROUP' && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Voucher category</label>
                  <input value={scopeCode} onChange={e => setScopeCode(e.target.value)} className={inputCls}
                    placeholder="The category code as the sales system sends it" />
                  <p className="mt-1 text-xs text-slate-400">Gift vouchers sold in this category post to this account (role VOUCHER_LIABILITY); other categories use the company default.</p>
                </div>
              )}
              {scopeType === 'COST_CENTER' && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Cost Centre</label>
                  <select value={costCenterId} onChange={e => setCostCenterId(e.target.value)} className={inputCls}>
                    <option value="">Select a cost centre…</option>
                    {costCenters.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Role</label>
                <select value={accountRole} onChange={e => setAccountRole(e.target.value)} className={inputCls}>
                  {roleOptions.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">GL Account</label>
                <select value={accountId} onChange={e => setAccountId(e.target.value)} className={inputCls}>
                  <option value="">Select an account…</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
              <button onClick={() => setEditing(false)} disabled={saving} className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
              <button onClick={save} disabled={!canSave || saving}
                className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                {saving ? <><Spinner size="sm" /> Saving…</> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
